import { useCallback, useEffect, useState } from 'react';
import { roles as rolesApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { Loading, Empty, Modal, Can, dateOnly } from '../components/Common';

/**
 * Roles & Permissions.
 *
 * A role is a named bundle of `<module>.<action>` keys. The matrix below is
 * built from GET /api/roles/permissions, so it always mirrors whatever the
 * server considers a valid permission - no hard-coded list on this side.
 */
export default function Roles() {
  const toast = useToast();

  const [catalogue, setCatalogue] = useState({ modules: [], templates: {} });
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // role object, or {} for new

  const load = useCallback(async () => {
    try {
      const [cat, roleList] = await Promise.all([rolesApi.catalogue(), rolesApi.list()]);
      setCatalogue(cat.data);
      setList(roleList.data.roles);
    } catch (err) {
      toast.error('Could not load roles', err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Roles & Permissions · UrbanClean'; }, []);

  const remove = async (role) => {
    if (!window.confirm(`Delete the role "${role.name}"?`)) return;
    try {
      await rolesApi.remove(role._id);
      toast.success('Role deleted');
      load();
    } catch (err) {
      toast.error('Could not delete the role', err.message);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Roles &amp; Permissions</h1>
          <div className="sub">
            Build a role, tick what it may do, then attach staff accounts to it under Staff.
          </div>
        </div>
        <Can permission="role.create">
          <button className="btn btn-blue" onClick={() => setEditing({ name: '', description: '', permissions: [] })}>
            + New role
          </button>
        </Can>
      </div>

      <div className="card">
        {loading ? <Loading /> : list.length === 0 ? (
          <Empty title="No roles yet" body="Create one to start handing out scoped staff logins." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr><th>Role</th><th>Permissions</th><th className="center">Staff</th><th>Created</th><th></th></tr>
              </thead>
              <tbody>
                {list.map((role) => (
                  <tr key={role._id}>
                    <td>
                      <div className="strong">{role.name}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{role.description}</div>
                      {role.isSystem ? <span className="badge badge-blue" style={{ marginTop: 4 }}>System</span> : null}
                    </td>
                    <td>
                      <span className="badge badge-gray">{role.permissions.length} permission(s)</span>
                      <div className="muted" style={{ fontSize: 11.5, marginTop: 4, maxWidth: 380 }}>
                        {role.permissions.slice(0, 6).join(', ')}
                        {role.permissions.length > 6 ? ` +${role.permissions.length - 6} more` : ''}
                      </div>
                    </td>
                    <td className="center">{role.staffCount}</td>
                    <td className="muted nowrap">{dateOnly(role.createdAt)}</td>
                    <td className="right nowrap">
                      <Can permission="role.update">
                        <button className="btn btn-sm btn-ghost" onClick={() => setEditing(role)}>Edit</button>
                      </Can>
                      {!role.isSystem ? (
                        <Can permission="role.delete">
                          <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => remove(role)}>
                            Delete
                          </button>
                        </Can>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RoleEditor
        role={editing}
        catalogue={catalogue}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </>
  );
}

function RoleEditor({ role, catalogue, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', description: '', permissions: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (role) {
      setForm({
        name: role.name || '',
        description: role.description || '',
        permissions: role.permissions || [],
      });
    }
  }, [role]);

  if (!role) return null;

  const has = (key) => form.permissions.includes(key);

  const toggle = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  /** Ticks or clears every action in one module at once. */
  const toggleModule = (module) => {
    const keys = module.actions.map((a) => `${module.key}.${a}`);
    const allOn = keys.every((k) => form.permissions.includes(k));
    setForm((f) => ({
      ...f,
      permissions: allOn
        ? f.permissions.filter((p) => !keys.includes(p))
        : [...new Set([...f.permissions, ...keys])],
    }));
  };

  const applyTemplate = (name) => {
    const permissions = catalogue.templates[name] || [];
    setForm((f) => ({ ...f, name: f.name || name, permissions }));
    toast.info(`Applied the "${name}" template`, `${permissions.length} permissions ticked`);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Give the role a name');
    setBusy(true);
    try {
      if (role._id) await rolesApi.update(role._id, form);
      else await rolesApi.create(form);
      toast.success(role._id ? 'Role updated' : 'Role created');
      onSaved();
    } catch (err) {
      toast.error('Could not save the role', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      wide
      title={role._id ? `Edit role — ${role.name}` : 'New role'}
      onClose={onClose}
      footer={<>
        <span className="muted" style={{ marginRight: 'auto', fontSize: 12.5 }}>
          {form.permissions.length} permission(s) selected
        </span>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save role'}
        </button>
      </>}
    >
      <div className="row">
        <div className="field">
          <label>Role name *</label>
          <input
            className="input"
            value={form.name}
            disabled={role.isSystem}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Counter Staff"
          />
        </div>
        <div className="field">
          <label>Description</label>
          <input
            className="input"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="What this role is for"
          />
        </div>
      </div>

      {Object.keys(catalogue.templates || {}).length ? (
        <div className="field">
          <label>Start from a template</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.keys(catalogue.templates).map((name) => (
              <button key={name} type="button" className="btn btn-sm btn-ghost" onClick={() => applyTemplate(name)}>
                {name}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="field">
        <label>Permissions</label>
        {catalogue.modules.map((module) => {
          const keys = module.actions.map((a) => `${module.key}.${a}`);
          const allOn = keys.every((k) => has(k));
          const someOn = keys.some((k) => has(k));

          return (
            <div key={module.key} className="perm-module">
              <div className="perm-head">
                <div>
                  <div className="perm-title">{module.label}</div>
                  <div className="muted mono" style={{ fontSize: 11 }}>{module.key}</div>
                </div>
                <button
                  type="button"
                  className={`btn btn-sm ${allOn ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => toggleModule(module)}
                >
                  {allOn ? 'Clear all' : someOn ? 'Select all' : 'Select all'}
                </button>
              </div>
              <div className="perm-actions">
                {module.actions.map((action) => {
                  const key = `${module.key}.${action}`;
                  return (
                    <label key={key} className={`perm-chip ${has(key) ? 'on' : ''}`}>
                      <input type="checkbox" checked={has(key)} onChange={() => toggle(key)} />
                      {has(key) ? '✓' : '＋'} {action}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
